import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { sanitizeToLineBreaksOnly } from '@cart-ui/RadioPayment/RadioPayment'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiGetFpPayment } from '@hooks-api/fortune/useApiGetFpPayment'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { PaymentMethodType, PaymentServiceType } from '@hooks-api/types/dataModel/payment'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Radio } from '@pets-ui/Radio/Radio'
import { Select } from '@pets-ui/Select/Select'
import { SelectOption } from '@pets-ui/SelectOption/SelectOption'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { typography } from '@pets-ui/tokens/typography.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { useCallback, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom-v5'
import { useContextPayment } from '../../../../fp-b2b-shared/components/PaymentProvider/PaymentProvider'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { paymentComponentStyle } from './PaymentMethodComponent'
const styles = stylex.create({
  bankCodeError: {
    borderColor: colors.red600,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
  descriptionHtml: {
    color: colors.grey500,
    fontSize: typography.sizeBody,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightBody,
  },
},
)

export const PaymentEcpayAtm = () => {
  const { control, setValue, watch } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const { selectedPayment, setSelectedPayment, paymentMethods, setPaymentExecution, setConfiguredPaymentModule, isSdkInitialized, setIsLoadingPayment } = useContextPayment()
  const { trigger: getPaymentToken } = useApiGetFpPayment()
  const { campaignId, campaignCode, platform } = useCheckout()

  const checked = selectedPayment?.service_type === PaymentServiceType.ECPAY && selectedPayment.type === PaymentMethodType.ATM
  const currentMethod = paymentMethods.find(method => method.service_type === PaymentServiceType.ECPAY && method.type === PaymentMethodType.ATM)
  const bankList = currentMethod?.metas?.banks?.filter(bank => bank.enabled) ?? []

  const { transactionId: paymentTransactionIdFromUrl } = useParams<{ transactionId?: string }>()
  const { orderId, orderType } = useFpB2bPaymentService({ campaignId, paymentTransactionIdFromUrl, campaignCode, platform })
  const handleSelectPayment = useCallbackEvent(async () => {
    try {
      if (!campaignCode || !currentMethod) {
        throw new Error('Missing campaign code, selectedPayment')
      }

      setSelectedPayment(currentMethod)

      setIsLoadingPayment(true)
      const token = await getPaymentToken({
        order_type: orderType,
        order_id: orderId,
        payment_method_id: currentMethod.id,
      })

      const { payment_transaction_id } = token.data.data
      setPaymentExecution({
        payment_transaction_id,
        service_pay_info: {
          bank_code: formValues.bank_code,
          pay_token: null,
          apple_pay_info: null,
        },
        user_payment_token_info: undefined,
        payment_success_url: '',
        payment_fallback_url: '',
      })
      setConfiguredPaymentModule(undefined)
    }
    catch (error) {
      console.error(`[PaymentEcpayAtm] ${error}`)
    }
    finally {
      setIsLoadingPayment(false)
    }
  })

  const handleChangeBankCode = useCallbackEvent((value: string) => {
    setValue('bank_code', value)
  })

  const handleInitPayment = useCallback(async () => {
    // NOTE: isSdkInitialized promises that the payment module is init after sdk init
    const shouldInit = selectedPayment?.service_type === PaymentServiceType.ECPAY && selectedPayment.type === PaymentMethodType.ATM && isSdkInitialized
    if (!shouldInit) return
    handleSelectPayment()
  }, [handleSelectPayment, isSdkInitialized, selectedPayment?.service_type, selectedPayment?.type])

  // NOTE: init for default payment
  useEffect(() => {
    handleInitPayment()
  }, [handleInitPayment])
  return (
    <Box component="label" rootStyle={[paymentComponentStyle.root, paymentComponentStyle.radioPaymentChecked]}>
      <VStack flexGrow={1} gap={2.5}>
        <HStack alignItems="center" gap={2}>
          <Radio
            name="paymentEcpayAtm"
            checked={checked}
            value="paymentEcpayAtm"
            onChange={handleSelectPayment}
          />
          <Text fontWeight={600} lineHeight="100%">ATM 虛擬帳號</Text>
          <Box flexGrow={1} />
          {/* endIcon */}
        </HStack>
        {
          checked
          && (
            <>
              <Controller
                control={control}
                name="bank_code"
                rules={{ required: '請選擇銀行' }}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <VStack gap={2} rootRef={fieldRefHelper(ref)}>
                    <Select
                      {...field}
                      rootRef={ref}
                      onChange={handleChangeBankCode}
                      placeholder="請選擇銀行"
                      rootStyle={error ? styles.bankCodeError : undefined}
                      data-testid="select-bank-code"
                    >
                      {bankList.length > 0
                        ? (
                          bankList.map(bank => (
                            <SelectOption key={bank.bank_code} value={bank.bank_code}>
                              {bank.name}
                            </SelectOption>
                          ))
                        )
                        : <></>}
                    </Select>
                    {error && <Text color={colors.red600}>{error.message}</Text>}
                  </VStack>
                )}
              />
            </>
          )
        }
        {currentMethod?.description && (
          <div {...stylex.props(styles.descriptionHtml)} dangerouslySetInnerHTML={{ __html: sanitizeToLineBreaksOnly(currentMethod.description) }} />
        )}
      </VStack>
    </Box>
  )
}
